import { openEditMode } from "components/NewForm/utils";
import AuthPage from "components/shared/AuthPage";
import DetailPanelScroll from "components/shared/DetailPanelScroll";
import ElementList from "components/shared/ElementList";
import type { FilterOrder } from "components/shared/ElementList/sortStorage";
import { LIST_SKELETON_COUNT } from "components/shared/TextSkeleton";
import Transferee from "components/Transferee";
import TransfereeListItem from "components/TransfereeListItem";
import TransfereesFormContainer from "containers/TransfereesFormContainer";
import useListItemPreview from "hooks/useListItemPreview";
import useSelectFirstRouteItem from "hooks/useSelectFirstRouteItem";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/store";
import type { ITransferee } from "store/transfereesStore";
import { fetchTransferees, fetchTransfereesTags, resetTransferees, setCurrentTransferee } from "store/transfereesStore";
import type { TParamsPagination } from "store/types";

// Transferees container page: includes list of transferees and their page.
const Transferees = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const params = useParams();
    const transfereeId = params.id ? Number(params.id) : undefined;
    const routeTransfereeId = transfereeId ?? null;

    /**
     * Object that contains refs for each item within list.
     */
    const elementListRef = {};

    const navigate = useNavigate();
    const {
        transferees,
        transfereesCount,
        transfereesStatus,
        transfereesTags,
        tagsStatus,
        transferee,
        isListRefreshing,
    } = useAppSelector((state) => state.transferees);
    const isListReady = transfereesStatus !== "idle" && !isListRefreshing;
    const isInitialLoad = transfereesStatus === "idle";

    const [editMode, setEditMode] = useState(false);

    const orderOptions: { name: string; label: string }[] = [
        { name: "createdAt", label: t("Filters.CreationDate") },
        { name: "name", label: t("Transferees.TransfereeName") },
    ];

    const defaultFilterOptions: FilterOrder = {
        sortBy: "createdAt",
        sortOrder: "desc",
    };

    useSelectFirstRouteItem(transferees, routeTransfereeId, "/transferees");

    const { selectItem: handleTransfereeSelection } = useListItemPreview({
        items: transferees,
        routeId: routeTransfereeId,
        currentId: transferee?.id,
        pathPrefix: "/transferees",
        enabled: isListReady,
        onPreview: (selectedTransferee) => dispatch(setCurrentTransferee(selectedTransferee)),
    });

    useEffect(() => {
        const options = { limit: 100, sortBy: "createdAt", sortOrder: "desc" };
        dispatch(fetchTransferees({ options }));
        return () => {
            dispatch(resetTransferees());
        };
    }, [dispatch]);

    useEffect(() => {
        if (tagsStatus === "idle") {
            dispatch(fetchTransfereesTags());
        }
    }, [dispatch, tagsStatus]);

    const isActiveTransfereeItem = (item: ITransferee) => {
        return (transfereeId ?? transferee?.id) === item.id;
    };

    const isTransfereeItemDisabled = (item: ITransferee) => {
        return editMode && (transfereeId ?? transferee?.id) !== item.id;
    };

    return (
        <AuthPage userAuthenticated={true}>
            <ElementList
                elements={transferees}
                currentElement={transfereeId ?? transferee?.id}
                setCurrentElement={(selectedTransferee: ITransferee) =>
                    dispatch(setCurrentTransferee(selectedTransferee))
                }
                elementCount={transfereesCount}
                elementsStatus={transfereesStatus}
                elementData={{
                    label: t("Transferees.Transferees"),
                    actionLabel: t("Transferees.NewTransferee"),
                    action: () => navigate("/transferees/new"),
                }}
                elementSearchFunc={(options: TParamsPagination) => dispatch(fetchTransferees({ options }))}
                orderOptions={orderOptions}
                defaultFilterOptions={defaultFilterOptions}
                editMode={editMode}
                tagList={transfereesTags}
                showTags
                isInitialLoad={isInitialLoad}
                showListLoading={!isListReady}
                listSkeleton={Array.from({ length: LIST_SKELETON_COUNT }, (_, index) => (
                    <TransfereeListItem key={`transferee-list-skeleton-${index}`} isLoading skeletonIndex={index} />
                ))}
            >
                <>
                    {transferees.map((item) => (
                        <TransfereeListItem
                            transferee={item}
                            key={`transferee-${item.id}`}
                            action={handleTransfereeSelection}
                            disabled={isTransfereeItemDisabled(item)}
                            active={isActiveTransfereeItem(item)}
                            ref={(el: HTMLDivElement | null) => (elementListRef[item.id] = el)}
                        />
                    ))}
                </>
            </ElementList>
            <DetailPanelScroll>
                <Transferee
                    openEditMode={() =>
                        openEditMode({
                            elementListRef: { ...elementListRef },
                            elementId: transfereeId?.toString() ?? "",
                            setEditMode,
                        })
                    }
                    editMode={editMode}
                    termCount={transferee?.transferTermCount ?? 0}
                    isListLoading={!isListReady}
                />
                {editMode && (
                    <TransfereesFormContainer
                        mode="edit-page"
                        editMode={editMode}
                        closeEditMode={() => setEditMode(false)}
                        transferee={transferee}
                    />
                )}
            </DetailPanelScroll>
        </AuthPage>
    );
};

export default Transferees;
